import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable } from "rxjs";
import { PERMISSIONS, STATIC_KEYS, USER_ROLE } from "src/constants/constants";
import { CommonService } from "./common.service";

@Injectable({
  providedIn: "root",
})
export class AuthGuard {
  constructor(private _router: Router, private _commonService: CommonService) {}

  /**
   * Determines whether a route can be activated based on authentication and permission.
   * @param route - The activated route snapshot.
   * @param state - The router state snapshot.
   * @returns True if access is granted, otherwise redirects or returns false.
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const authToken = this._commonService.getItem(STATIC_KEYS.IDP_AUTH_TOKEN);
    const loginUser = this._commonService.getItem(
      STATIC_KEYS.LOGGED_IN_USER_INFO,
      true
    );
    const firstRole = loginUser?.roles?.[0];

    if (!authToken) return this.redirectTo("/");

    const { identifierName = [], permissions: requiredPermissions = [] } =
      route.data as RouteData;
    // console.log(">>>>>>>>>>requiredPermissions", {
    //   requiredPermissions,
    //   identifierName,
    //   url: state.url,
    // });
    if ([USER_ROLE.ADMIN, USER_ROLE.SYSTEM_ADMIN].includes(firstRole?.slug)) {
      return true;
    }
    if (requiredPermissions.length) {
      if (this.hasPermission(requiredPermissions, identifierName)) {
        return true;
      }
      return this.handleUnauthorizedAccess();
    }

    return true;
  }

  /**
   * Checks if the current user has required permissions.
   * @param requiredPermissions - Array of required actions like 'list', 'edit'.
   * @param identifierName - Resource identifier like 'r-artefacts'.
   * @returns True if user has any of the required permissions.
   */
  private hasPermission(
    requiredPermissions: string[],
    identifierName: string[] = []
  ): boolean {
    const userPermissions = this._commonService.getPermissions();
    // Example: { "r-artefacts": ["list", "edit"], "r-users": ["add"] }

    if (PERMISSIONS.ALL in userPermissions || "r-all" in userPermissions) {
      return true;
    }

    if (identifierName.length) {
      return identifierName.some((identName) => {
        const grantedActions = userPermissions[identName] || [];

        return requiredPermissions.some((action) =>
          grantedActions.includes(action)
        );
      });
    }

    // Fallback in case identifierName is not used (rare)
    const permissionKeys = Object.keys(userPermissions);
    return requiredPermissions.some((perm) => permissionKeys.includes(perm));
  }

  /**
   * Handle unauthorized access by redirecting to the error page
   * @returns boolean - Always returns false
   */
  private handleUnauthorizedAccess(): boolean {
    this._router.navigate(["/admin/error"]);
    return false;
  }

  /**
   * Redirect to a specific route
   * @param path - The route to navigate to
   * @returns boolean - Always returns false
   */
  private redirectTo(path: string): boolean {
    this._router.navigate([path]);
    return false;
  }
}

export interface RouteData {
  title: string;
  permissions?: string[]; // Use RESOURCE_ACTION values
  identifierName?: string[]; // Use ROLE_RESOURCE_PERMISSIONS values
}
